import { useEffect, useRef, type ReactNode } from 'react';
import { useQuery } from '@apollo/client';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Bell, X } from 'lucide-react';
import {
  type Notification,
  type ToastSurface,
  generateNotificationMessage,
  getActorDisplayName,
  getNotificationActionText,
  normalizeNotificationType,
  normalizeTargetType,
  buildCommunityPostCommentPath,
  buildImageCommentPath,
  resolveToastSurface,
  shouldToast,
  welcomeRecap,
  welcomeToastCta,
} from '@arthelper/shared';
import {
  ENGAGEMENT_TOAST_ID,
  ENGAGEMENT_TOAST_TYPE,
  type EngagementToastState,
  coalesceEngagementToast,
  dismissEngagementToast,
  engagementToastDuration,
  engagementToastHeadline,
  engagementToastSnippet,
  isInstantEngagement,
} from '@arthelper/shared/engagement-toast';
import { NOTIFICATION_ADDED_SUBSCRIPTION, NOTIFICATIONS_QUERY, SOCIAL_BADGE_COUNTS_QUERY } from '~/graphql/queries';
import { useAuth } from '~/context/auth';
import { useMessagingDock } from '~/context/messagingDock';
import { useAnalytics } from '~/context/analytics';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { useResilientGraphQLSubscription } from '~/hooks/useResilientGraphQLSubscription';
import { getPublicSiteBaseUrl } from '~/utils/index';
import { useArtyFlyoutOptional } from '~/components/arty/flyout';
import { isSparklink } from '~/lib/notification-sparklink';

const INBOX_HREF = '/notifications';
const NETWORK_REQUESTS_HREF = '/network/requests';
const WELCOME_TOAST_ID = 'welcome-notification';
const WELCOME_TOAST_DURATION_MS = 8000;
const WELCOME_RECAP_TYPE = 'welcome_recap';
const WELCOME_RECAP_LIMIT = 50;

type ToastClick =
  | { kind: 'href'; href: string }
  | { kind: 'thread'; threadId: string }
  | { kind: 'arty'; sparklink: string };

interface NotificationAddedData {
  notificationAdded: {
    notification: Notification;
    unreadCount: number;
  };
}

function townSquareBase(): string {
  return getPublicSiteBaseUrl() || 'https://www.arthelper.com';
}

function metaString(metadata: Record<string, unknown> | null | undefined, key: string): string | undefined {
  const value = metadata?.[key];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function commentPermalink(notification: Notification): string | null {
  const type = normalizeNotificationType(notification.type);
  const targetType = normalizeTargetType(notification.targetType);
  const metadata = notification.metadata || {};
  const baseUrl = townSquareBase();
  const communitySlug = metaString(metadata, 'communitySlug');
  const communityPostSlug = metaString(metadata, 'communityPostSlug') || metaString(metadata, 'postSlug');

  if ((type === 'community_mention' || type === 'mention') && communitySlug && communityPostSlug) {
    if (targetType === 'community_comment' && notification.targetId) {
      return `${baseUrl}${buildCommunityPostCommentPath(communitySlug, communityPostSlug, notification.targetId)}`;
    }
    return `${baseUrl}/community/${communitySlug}/post/${communityPostSlug}`;
  }

  if (type === 'community_reply' && communitySlug && metaString(metadata, 'postSlug') && notification.targetId) {
    return `${baseUrl}${buildCommunityPostCommentPath(
      communitySlug,
      metaString(metadata, 'postSlug')!,
      notification.targetId
    )}`;
  }

  const profileSlug = metaString(metadata, 'profileSlug');
  const imageSlug = metaString(metadata, 'imageSlug');
  const commentId = metaString(metadata, 'commentId');

  if ((type === 'comment' || type === 'comment_reply') && targetType === 'image' && profileSlug && imageSlug) {
    if (commentId) return `${baseUrl}${buildImageCommentPath(profileSlug, imageSlug, commentId)}`;
    return `${baseUrl}/${profileSlug}/image/${imageSlug}`;
  }

  if ((type === 'comment' || type === 'comment_reply') && targetType === 'post' && notification.targetId) {
    const anchor = commentId ? `#comment-${commentId}` : '';
    return `${baseUrl}/post/${notification.targetId}${anchor}`;
  }

  if (
    (type === 'comment' || type === 'comment_reply') &&
    (targetType === 'community_post' || targetType === 'community_comment') &&
    communitySlug &&
    communityPostSlug
  ) {
    const id = commentId || notification.targetId;
    if (id) return `${baseUrl}${buildCommunityPostCommentPath(communitySlug, communityPostSlug, id)}`;
    return `${baseUrl}/community/${communitySlug}/post/${communityPostSlug}`;
  }

  return null;
}

function likedPermalink(notification: Notification): string | null {
  const targetType = normalizeTargetType(notification.targetType);
  const metadata = notification.metadata || {};
  const baseUrl = townSquareBase();
  const profileSlug = metaString(metadata, 'profileSlug');
  const imageSlug = metaString(metadata, 'imageSlug');
  const ownerSlug = notification.user?.profile?.slug;
  const commentId = metaString(metadata, 'commentId');
  const postId = metaString(metadata, 'postId');

  if (targetType === 'comment' && postId && notification.targetId) {
    return `${baseUrl}/post/${postId}#comment-${notification.targetId}`;
  }

  if (targetType === 'post' && notification.targetId) {
    return `${baseUrl}/post/${notification.targetId}`;
  }

  if (targetType === 'image') {
    if (profileSlug && imageSlug) return `${baseUrl}/${profileSlug}/image/${imageSlug}`;
    if (ownerSlug && imageSlug) return `${baseUrl}/${ownerSlug}/image/${imageSlug}`;
    if (ownerSlug && notification.targetId) return `${baseUrl}/${ownerSlug}/image/img-${notification.targetId}`;
  }

  const communitySlug = metaString(metadata, 'communitySlug');
  const postSlug = metaString(metadata, 'postSlug') || metaString(metadata, 'communityPostSlug');
  if (targetType === 'community_post' && communitySlug && postSlug) {
    return `${baseUrl}/community/${communitySlug}/post/${postSlug}`;
  }
  const communityCommentId = commentId || notification.targetId;
  if (targetType === 'community_comment' && communitySlug && postSlug && communityCommentId) {
    return `${baseUrl}${buildCommunityPostCommentPath(communitySlug, postSlug, communityCommentId)}`;
  }

  return null;
}

function resolveToastClick(notification: Notification): ToastClick {
  const type = normalizeNotificationType(notification.type);
  const metadata = notification.metadata || {};

  if (type === 'spark_review_ready') {
    const sparklinkUrl = metaString(metadata, 'sparklinkUrl');
    if (isSparklink(sparklinkUrl)) return { kind: 'arty', sparklink: sparklinkUrl };
    if (sparklinkUrl) return { kind: 'href', href: sparklinkUrl };
    return { kind: 'href', href: INBOX_HREF };
  }

  if (type === 'critique_posted') {
    const href = likedPermalink(notification);
    if (href) return { kind: 'href', href };
    const postUrl = metaString(metadata, 'postUrl');
    if (postUrl) return { kind: 'href', href: postUrl };
    return { kind: 'href', href: INBOX_HREF };
  }

  if (type === 'dm_received') {
    const threadId = metaString(metadata, 'messageId') || notification.targetId || undefined;
    if (threadId) return { kind: 'thread', threadId };
    return { kind: 'href', href: INBOX_HREF };
  }

  if (type === 'connection_request') return { kind: 'href', href: NETWORK_REQUESTS_HREF };

  if (type === 'follow' || type === 'connection_accepted') {
    const slug = notification.actor?.profile?.slug;
    if (slug) return { kind: 'href', href: `${townSquareBase()}/${slug}` };
  }

  if (
    type === 'comment' ||
    type === 'comment_reply' ||
    type === 'community_reply' ||
    type === 'mention' ||
    type === 'community_mention'
  ) {
    const href = commentPermalink(notification);
    if (href) return { kind: 'href', href };
  }

  if (type === 'like') {
    const href = likedPermalink(notification);
    if (href) return { kind: 'href', href };
  }

  return { kind: 'href', href: INBOX_HREF };
}

function actorInitials(name: string): string {
  return (
    name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join('') || 'U'
  );
}

function previewText(metadata: Record<string, unknown> | null | undefined): string | undefined {
  return metaString(metadata, 'commentText') || metaString(metadata, 'contentSnippet');
}

function isInAppHref(href: string): boolean {
  return href.startsWith('/') && !href.startsWith('//');
}

function toastOptions(surface: ToastSurface, notificationId: string) {
  if (surface === 'welcome') {
    return { id: WELCOME_TOAST_ID, position: 'top-center' as const, duration: WELCOME_TOAST_DURATION_MS };
  }
  return { id: notificationId, position: 'bottom-right' as const };
}

function headlineFor(notification: Notification): ReactNode {
  const actionText = getNotificationActionText(notification);
  if (!actionText) {
    return generateNotificationMessage(notification);
  }
  return (
    <>
      <span className="font-semibold">{getActorDisplayName(notification)}</span> {actionText}
    </>
  );
}

function AvatarImage({ avatarUrl, name }: { avatarUrl?: string | null; name: string }) {
  if (avatarUrl) {
    return <img src={avatarUrl} alt="" width={40} height={40} className="h-full w-full object-cover" />;
  }
  return <>{actorInitials(name)}</>;
}

function ToastAvatar({ notification }: { notification: Notification }) {
  return <AvatarImage avatarUrl={notification.actor?.profile?.avatarUrl} name={getActorDisplayName(notification)} />;
}

/** The recap reads the newest WELCOME_RECAP_LIMIT unread rows, so a full page means "at least". */
function recapCount(count: number): string {
  if (count >= WELCOME_RECAP_LIMIT) {
    return `${WELCOME_RECAP_LIMIT}+`;
  }
  return String(count);
}

interface ToastCardProps {
  toastId: string;
  avatar: ReactNode;
  headline: ReactNode;
  snippet?: string | null;
  cta?: string;
  onActivate: () => void;
  onDismiss?: () => void;
}

function ToastCard({ toastId, avatar, headline, snippet, cta, onActivate, onDismiss }: ToastCardProps) {
  return (
    <div
      role="button"
      tabIndex={0}
      className="flex w-[356px] max-w-[calc(100vw-2rem)] cursor-pointer items-start gap-3 rounded-lg border border-gray-200 bg-white p-3 shadow-lg"
      onClick={onActivate}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          event.currentTarget.click();
        }
      }}
    >
      <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded-full bg-gray-100 flex items-center justify-center text-xs font-medium text-gray-600">
        {avatar}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-gray-900">{headline}</p>
        {snippet ? <p className="mt-1 text-sm text-gray-500 line-clamp-2 break-words">{snippet}</p> : null}
        {cta ? <p className="mt-1 text-sm font-medium text-green-700">{cta} →</p> : null}
      </div>
      <button
        type="button"
        aria-label="Dismiss"
        className="flex-shrink-0 rounded p-0.5 text-gray-400 hover:text-gray-700"
        onClick={(event) => {
          event.stopPropagation();
          onDismiss?.();
          toast.dismiss(toastId);
        }}
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

/**
 * One recap toast when a member arrives in the wizard with unread notifications waiting
 * (back from an email the next day, or Nick's welcome request from signup): live toasts only
 * cover what lands mid-session. Once per page load; no-cache so the inbox's cached list of
 * every notification is never overwritten by this unread-only slice. Returns a canceller: a
 * live wizard toast that lands first makes a slower recap stale, so it must not replace it.
 */
function useWelcomeRecap(enabled: boolean) {
  const { amplitude } = useAnalytics();
  const shownRef = useRef(false);
  const { data } = useQuery(NOTIFICATIONS_QUERY, {
    variables: { limit: WELCOME_RECAP_LIMIT, offset: 0, isRead: false },
    skip: !enabled || shownRef.current,
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (!enabled || shownRef.current || !data) {
      return;
    }
    shownRef.current = true;
    const recap = welcomeRecap<Notification>(data.notifications?.notifications ?? []);
    if (!recap) {
      return;
    }
    const track = { notification_type: WELCOME_RECAP_TYPE, surface: 'welcome' as const };
    amplitude.track('notification_toast_shown', track);
    const onActivate = () => {
      amplitude.track('notification_toast_clicked', track);
      toast.dismiss(WELCOME_TOAST_ID);
    };
    const single = recap.count === 1;
    toast.custom(
      () => (
        <ToastCard
          toastId={WELCOME_TOAST_ID}
          avatar={single ? <ToastAvatar notification={recap.first} /> : <Bell className="h-5 w-5" />}
          headline={single ? headlineFor(recap.first) : `You have ${recapCount(recap.count)} new notifications`}
          snippet={single ? previewText(recap.first.metadata) : undefined}
          cta={single ? welcomeToastCta(recap.first.type) : 'Finish setup to see them'}
          onActivate={onActivate}
        />
      ),
      toastOptions('welcome', WELCOME_TOAST_ID)
    );
  }, [enabled, data, amplitude]);

  return () => {
    shownRef.current = true;
  };
}

export function NotificationToastListener() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { openThread, threadPanel } = useMessagingDock();
  const { amplitude } = useAnalytics();
  const { enabled: connectionsEnabled } = useFeatureFlag('connections_and_dms');
  const threadIdRef = useRef(threadPanel?.threadId);
  threadIdRef.current = threadPanel?.threadId;
  const flyout = useArtyFlyoutOptional();
  const flyoutRef = useRef(flyout);
  flyoutRef.current = flyout;
  const engagementRef = useRef<EngagementToastState | null>(null);

  // Onboarding is a guided flow, so wizard toasts are teasers that pull the member toward
  // finishing: one at a time, top-center clear of the fixed mobile nav, with a "Finish setup"
  // line, and a click only dismisses. Replying from another page ends the wizard for good
  // (/welcome stops being reachable once they have community activity). Spark and the
  // critique receipt stay silent there — see shouldToast.
  const surface = resolveToastSurface(location.pathname);
  const onWelcome = surface === 'welcome';
  const cancelRecap = useWelcomeRecap(Boolean(user) && onWelcome);
  const dockHidden =
    location.pathname.startsWith('/messages') ||
    onWelcome ||
    location.pathname.startsWith('/post') ||
    /^\/art(\/|$)/.test(location.pathname);
  const dockMountedRef = useRef(connectionsEnabled && !dockHidden);
  dockMountedRef.current = connectionsEnabled && !dockHidden;

  // A new member's upvotes and reactions inside 60 s are ONE popup that sonner updates in
  // place (#4096). The decisions live in shared/engagement-toast.ts; this only renders.
  // targetPath is a town-square path, so it opens on the town-square origin.
  function showEngagementToast(notification: Notification) {
    const now = Date.now();
    const previous = engagementRef.current;
    const next = coalesceEngagementToast(previous, notification, now);
    if (!next || next === previous) return;
    engagementRef.current = next;
    if (next.dismissed) return;
    const track = { notification_type: ENGAGEMENT_TOAST_TYPE, surface: 'app' as const };
    if (previous?.windowStartedAt !== next.windowStartedAt) {
      amplitude.track('notification_toast_shown', track);
    }

    const { actor, action } = engagementToastHeadline(next);
    const href = next.targetHref ? `${townSquareBase()}${next.targetHref}` : INBOX_HREF;
    const onActivate = () => {
      amplitude.track('notification_toast_clicked', track);
      toast.dismiss(ENGAGEMENT_TOAST_ID);
      if (isInAppHref(href)) {
        navigate(href, { state: { from: location.pathname } });
        return;
      }
      window.location.href = href;
    };
    const onDismiss = () => {
      engagementRef.current = dismissEngagementToast(engagementRef.current);
    };

    toast.custom(
      () => (
        <ToastCard
          toastId={ENGAGEMENT_TOAST_ID}
          avatar={<AvatarImage avatarUrl={next.actors[0]?.avatarUrl} name={actor} />}
          headline={
            <>
              <span className="font-semibold">{actor}</span> {action}
            </>
          }
          snippet={engagementToastSnippet(next)}
          onActivate={onActivate}
          onDismiss={onDismiss}
        />
      ),
      { id: ENGAGEMENT_TOAST_ID, position: 'bottom-right', duration: engagementToastDuration(next, now) }
    );
  }

  useResilientGraphQLSubscription<NotificationAddedData>(NOTIFICATION_ADDED_SUBSCRIPTION, {
    skip: !user,
    label: 'notificationAdded',
    onRecovered: (attempts) => {
      amplitude.track('notification_socket_recovered', { attempts });
    },
    onLost: (attempt) => {
      // Once per outage; the retry count rides on notification_socket_recovered.
      if (attempt !== 1) return;
      amplitude.track('notification_socket_lost', { attempt });
    },
    onData: ({ client, data }) => {
      const payload = data.data?.notificationAdded;
      if (!payload?.notification) return;

      const { notification, unreadCount } = payload;

      // cache.modify, not cache.updateQuery: updateQuery silently no-ops when the
      // document isn't fully satisfiable from cache. Modifying the ROOT_QUERY
      // `notifications` field updates every cached arg-variant of the connection
      // (unreadCount is the same global count in all of them), so the cache-only
      // AppSidebar badge ticks at toast time instead of waiting for the 60s poll.
      client.cache.modify({
        fields: {
          notifications(existing: any) {
            if (!existing || typeof existing !== 'object') return existing;
            return { ...existing, unreadCount };
          },
        },
      });

      const type = normalizeNotificationType(notification.type);
      if (type === 'connection_request' || type === 'connection_accepted') {
        void client.refetchQueries({ include: [SOCIAL_BADGE_COUNTS_QUERY] });
      }

      // Only on 'app': on /welcome these types are not toast types, so shouldToast drops them.
      if (surface === 'app' && isInstantEngagement(notification)) {
        showEngagementToast(notification);
        return;
      }

      if (!shouldToast({ type: notification.type, surface })) return;

      if (normalizeNotificationType(notification.type) === 'dm_received') {
        const threadId = metaString(notification.metadata, 'messageId') || notification.targetId || undefined;
        if (threadId && threadIdRef.current === threadId) return;
      }

      const options = toastOptions(surface, notification.id);
      const toastId = options.id;
      const track = { notification_type: notification.type, surface };

      amplitude.track('notification_toast_shown', track);
      if (onWelcome) cancelRecap();

      const onActivate = () => {
        amplitude.track('notification_toast_clicked', track);
        toast.dismiss(toastId);
        if (onWelcome) return;
        const dest = resolveToastClick(notification);
        if (dest.kind === 'thread') {
          if (dockMountedRef.current) {
            openThread(dest.threadId, { source: 'toast' });
          } else {
            navigate(`/messages/${dest.threadId}`, { state: { from: location.pathname } });
          }
          return;
        }
        if (dest.kind === 'arty') {
          if (flyoutRef.current) {
            flyoutRef.current.open({ sparklink: dest.sparklink, source: 'notification_toast' });
            return;
          }
          window.location.href = dest.sparklink;
          return;
        }
        if (isInAppHref(dest.href)) {
          navigate(dest.href, { state: { from: location.pathname } });
        } else {
          window.location.href = dest.href;
        }
      };

      toast.custom(
        () => (
          <ToastCard
            toastId={toastId}
            avatar={<ToastAvatar notification={notification} />}
            headline={headlineFor(notification)}
            snippet={previewText(notification.metadata)}
            cta={onWelcome ? welcomeToastCta(notification.type) : undefined}
            onActivate={onActivate}
          />
        ),
        options
      );
    },
  });

  return null;
}
